'use client'

import * as React from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Link } from '@repo/web-ui/components/link'
import { useTranslations, useLocale } from 'next-intl'

import { Table as MyAccountTable } from './table'
import { buttonVariants } from './button'
import type { CfOrder } from '@web/gen/types'
import { useListUserOrdersQuery } from '@web/gen/subscriptions'
import { MyAccountPageSkeletonInner } from './my-account-page-skeleton'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { keepPreviousData } from '@tanstack/react-query'

const columnHelper = createColumnHelper<CfOrder>()

export function OrdersTable() {
  const locale = useLocale()
  const t = useTranslations('pages.my_account.pages.orders')
  const t2 = useTranslations('pages.my_account')

  const [orders, setOrders] = React.useState<CfOrder[]>([])
  const [cursor, setCursor] = React.useState<string | null>(null) // null = initial page
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(true)

  // Keep latest flags and nextCursor in refs to avoid effect churn
  const nextCursorRef = React.useRef<string | null>(null)
  const isFetchingRef = React.useRef(false)
  const isLoadingMoreRef = React.useRef(false)
  const hasMoreRef = React.useRef(true)
  const isErrorRef = React.useRef(false)
  const refetchRef = React.useRef<() => unknown>(() => null)

  const { data, isLoading, isError, refetch, isFetching } = useListUserOrdersQuery(
    { cursor },
    { placeholderData: keepPreviousData },
  )

  // keep refs in sync with latest values
  React.useEffect(() => {
    isFetchingRef.current = isFetching
    isLoadingMoreRef.current = isLoadingMore
    hasMoreRef.current = hasMore
    isErrorRef.current = isError
    refetchRef.current = refetch
  }, [isFetching, isLoadingMore, hasMore, isError, refetch])

  // Merge incoming data; DO NOT set cursor here
  React.useEffect(() => {
    if (!data?.listUserOrders) {
      return
    }

    setOrders(prev => {
      if (!data.listUserOrders.orders?.length) {
        return prev
      }
      const seen = new Set(prev.map(o => o.id))
      const merged = [...prev]
      for (const order of data.listUserOrders.orders) {
        if (!seen.has(order.id)) {
          merged.push(order)
          seen.add(order.id)
        }
      }
      return merged
    })

    // Update next cursor & hasMore
    const next = data.listUserOrders.nextCursor ?? null
    nextCursorRef.current = next
    setHasMore(next !== null)

    // page finished loading
    setIsLoadingMore(false)
  }, [data])

  // If the last request errored out and we're idle, allow another attempt
  React.useEffect(() => {
    if (isError && !isFetching) {
      setIsLoadingMore(false)
    }
  }, [isError, isFetching])

  // Stable observer: created once
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    if (!sentinelRef.current) {
      return
    }
    const node = sentinelRef.current

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (!entry.isIntersecting) {
          return
        }

        // Guards
        if (isFetchingRef.current || isLoadingMoreRef.current) {
          return
        }
        if (!hasMoreRef.current) {
          return
        }

        setIsLoadingMore(true)

        if (isErrorRef.current) {
          // retry the last failed page
          refetchRef.current()
          return
        }

        const next = nextCursorRef.current
        if (next === null) {
          // no more pages
          setIsLoadingMore(false)
          setHasMore(false)
          return
        }

        // trigger the next page fetch
        setCursor(next)
      },
      { root: null, threshold: 0.1 },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [isFetching, orders.length])

  if (isLoading && orders.length === 0) {
    return <MyAccountPageSkeletonInner />
  }

  return (
    <div className="flex flex-col gap-10">
      <div className="@lg:mx-0 @lg:px-0 -mx-6 overflow-x-scroll px-6 pb-6">
        <MyAccountTable
          className="min-w-full"
          data={orders}
          columns={[
            columnHelper.accessor('id', {
              header: t('table.order'),
              cell: info => (
                <Link
                  href={addOrReplaceLocaleInUrl(
                    `${AppRoutes.MY_ACCOUNT_VIEW_ORDER}${info.row.original.id}`,
                    locale,
                  )}
                  className="text-cf-sf-iceberg-500"
                >
                  #{info.getValue()}
                </Link>
              ),
            }),
            columnHelper.accessor('date', {
              header: t('table.date'),
              cell: info => {
                // Your timestamp in seconds
                const timestamp = info.getValue()
                if (!timestamp) {
                  return '-'
                }
                const date = new Date(timestamp * 1000)
                return date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              },
            }),
            columnHelper.accessor('status', {
              header: t('table.status'),
              cell: info => <div dangerouslySetInnerHTML={{ __html: info.getValue() }} />,
            }),
            columnHelper.accessor('total', {
              header: t('table.total'),
              cell: info => <div dangerouslySetInnerHTML={{ __html: info.row.original.total }} />,
            }),
            columnHelper.display({
              id: 'actions',
              cell: info => (
                <div className="flex justify-end">
                  <div className="flex flex-col gap-2">
                    {info.row.original.needsPayment && info.row.original.paymentUrl ? (
                      <Link
                        className={buttonVariants({ variant: 'green', size: 'micro' })}
                        href={info.row.original.paymentUrl}
                      >
                        {t2('widgets.pay')}
                      </Link>
                    ) : null}
                    <Link
                      className={buttonVariants({ variant: 'outline', size: 'micro' })}
                      href={addOrReplaceLocaleInUrl(
                        `${AppRoutes.MY_ACCOUNT_VIEW_ORDER}${info.row.original.id}`,
                        locale,
                      )}
                    >
                      {t2('widgets.view')}
                    </Link>
                  </div>
                </div>
              ),
            }),
          ]}
          emptyPlaceholder={
            <div className="text-cf-grey-1000 text-14">
              {t.rich('empty', {
                link: chunks => (
                  <Link href={addOrReplaceLocaleInUrl(AppRoutes.SEARCH)} className="text-[#59B89D]">
                    {chunks}
                  </Link>
                ),
              })}
            </div>
          }
        />
        <div ref={sentinelRef} className="py-6 flex items-center justify-center gap-8">
          {isLoadingMore ? <Spinner className="h-4 w-4" /> : null}
        </div>
      </div>
    </div>
  )
}
